import React, { useState, useEffect, useRef } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import { compose } from 'redux';
import {
  Typography,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  Tooltip,
  Divider,
  Input,
  CircularProgress,
  IconButton, 
  Grow
} from '@material-ui/core';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@material-ui/icons/RadioButtonChecked';
import RefreshIcon from '@material-ui/icons/Refresh';
import NumberFormat from 'react-number-format';
import _ from 'lodash';
import classNames from 'classnames';
import { Enums } from 'utilities/enums';
import { ToCurrencyString, ToCurrencyStringWithDecimals, ToPercentageStringWithDecimals } from 'services/formatService';
import { formatValue } from 'utilities/format';

const styles = theme => ({
  disabledText: {
    color: 'gray',
  },
  inputTitle: {
    display: 'flex',
    justifyContent: 'start',
    alignItems: 'center',
    paddingTop: '5px',
  },
  inputsSubheader: {
    marginRight: 8,
    fontWeight: 'bold',
    color: theme.palette.red.main,
    fontSize: '12px',
  },
  subheaderCircle: {
    fill: theme.palette.red.main
  },
  inputsSubheaderSelected: {
    marginRight: 8,
    fontWeight: 'bold',
    fontSize: '12px',
  },
  radioGroup: {
    width: '100%',
    display: 'flex',
    flexDirectino: 'column'
  },
  // formControlLabelRoot is the style for a whole radio + label combo row
  formControlLabelRoot: {
    height: '22px',
    margin: '0',
    fontSize: '12px'
  },
  selectedFormControlLabelRoot: {
    height: '22px',
    margin: '0',
    background: '#e1f2fb'
  },
  // formControlLabel is the style for a whole radio label container
  formControlLabel: {
    width: '100%',
    fontSize: '12px'
  },
  // This defines style for the actual label part (text content + [textInput])
  formControlLabelContent: {
    display: 'grid',
    gridTemplateColumns: '130px 60px auto',
    gridTemplateRows: '17px',
    alignItems: 'center'
  },
  formControlLabelContentCol1: {
    gridColumnStart: 1,
    gridColumnEnd: 1
  },
  formControlLabelContentCol2: {
    gridColumnStart: 2,
    gridColumnEnd: 2,
    justifySelf: 'end'
  },
  formControlLabelContentCol3: {
    gridColumnStart: 3,
    gridColumnEnd: 3,
    // justifySelf: 'end',
    textAlign: 'end',
    width: '100%'
  },
  ancillary: {
    fontSize: '9px',
    color: 'gray'
  },
  radioInputRoot: {
    width: '100%'
  },
  radioInputsTextField: {
    // width: '65px',
    margin: '0px',
    padding: '0px',
    textAlign: 'end',
    fontSize: '12px',
    zIndex: '2' // need to set this otherwise calling focus() method on text control will have no effect
  },
  radioInputsTextFieldError: {
    color: theme.palette.red.main
  },
  divider: {
    marginTop: '2px'
  },
  refreshTaxesDisabled: {
    color: 'rgba(0, 0, 0, 0.14)!important'
  }
})

const findEnumTypeByInputGroupName = (inputGroupName) => {
  switch (inputGroupName) {
    case 'taxes':
      return Enums.TaxesType;
    case 'hoa':
      return Enums.HoaType;
    case 'repairEstimate':
      return Enums.RepairEstimateType;
    case 'rent':
      return Enums.RentType;
    case 'afterRepairValue':
      return Enums.AfterRepairValueType;
    case 'closingCosts':
      return Enums.ClosingCostsType;
    case 'bidPrice':
      return Enums.BidPriceType;
    default:
      return null;
  }
}

const PropertyUWProformaInput = ({ 
  classes,
  valuationId,
  radioValuesArray,
  inputSubheader,
  inputGroup,
  isDisabled,
  required = false,
  onInputChange,
  onKeyDown,
  onError,
  inputValue,
  inputValueType,
  ancillary,
  isLoading,
  onRefreshModelTax,
  isRefreshingModelTax,
  disableTaxRefresh,
  userHasUnderwriteReadOnlyRole,
  valuationInputSetting,
  setInputLimitError
}) => {
  const _valueTypeEnum = useRef(findEnumTypeByInputGroupName(inputGroup)); // we only want to instantiate _valueTypeEnum once and keep it the same across all rerenders.
  const [customInput, setCustomInput] = useState(null);  // this is the state to hold value of NumberFormat control input
  let _textInputRef = useRef(null); // instantiate a refence variable and later assign NumberFormat control to it

  useEffect(() => {
    // Only update customInput from inputValue when initiating the component, after that we will always update cutomInput locally first and then update transientValuation(namely inputValue)
    if (inputValue !== customInput) {
      if (inputValueType === _valueTypeEnum.current?.UserDictate) // only initialize customInput with inputValue when inputValue is of type 'UserDictate' or null.
      {
        setCustomInput(inputValue);
      }
    }
  }, [inputValue, inputValueType]);

  useEffect(() => {
    if (inputValueType !== 0) {
      setCustomInput('');
    }
  }, [valuationId])

  const onTextFieldValueChanged = (event) => {
    if (!event.target.value) {
      setCustomInput(null);
      onInputChange({ inputGroupName: inputGroup, selectedRadioValue: null, selectedRadioType: _valueTypeEnum.current.UserDictate }); // callback from parent to update transientValuation
    }
    else {
      const numberValue = parseFloat(String(event.target.value).replace(/\$|,/g, ''));  // Replace '$' and ',' with '' ---> '$1,569' to '1569'  
      setCustomInput(numberValue);
      onInputChange({ inputGroupName: inputGroup, selectedRadioValue: numberValue, selectedRadioType: _valueTypeEnum.current.UserDictate }); // callback from parent to update transientValuation
    }
  };

  const onSelectedRadioChanged = (event) => {
    const selectedRadioTypeInt = parseInt(event.target.value)
    if (selectedRadioTypeInt === _valueTypeEnum.current.UserDictate) {
      onInputChange({ inputGroupName: inputGroup, selectedRadioValue: customInput, selectedRadioType: _valueTypeEnum.current.UserDictate }); // callback from parent to update transientValuation
    }
    else {
      const found = radioValuesArray.find(el => el.type === selectedRadioTypeInt);
      if (found) {
        onInputChange({ inputGroupName: inputGroup, selectedRadioValue: found.value, selectedRadioType: found.type }); // callback from parent to update transientValuation
      }
      else {
        onError(`Cannot find ${inputGroup} radio type for ${String(event.target.value)}`);
      }
    }
  }

  // Pass the key down even to parent keydown handler, mainly to trigger calculate method on pressing 'enter'
  const onTextFieldKeyDown = (event) => {
    onKeyDown(event);
  }

  const onRefreshModelTaxClick = (event) => {
    onRefreshModelTax();
  }

  // Select the custom input value(or focus on the empty input box if there is no custom input value) when user click custom label
  const onCustomLabelClick = (event) => {
    _textInputRef.current.select();
  }

  const onCustomInputClick = (event) => {
    // This is to prevent when user click on custom input and the event propagate to parrent custom label 
    // and then alwasy select the whole input number instead of allowing user to only highlight and change a part of the custom input
    event.stopPropagation();
  }

  const compareInputValueToRadioValue = (inputValue, radioValue) => {
    // Both values are allowed to be null
    // If both are null or undefined, then we consider they the same
    if (inputValue == null && radioValue == null) return true;
    // Since all inputs are dollar amoutns, if both have value, we compare the value by rounding them to 0 decimals
    // NOTE: We do this to avoid any discrepancy between saved/calculated decimal numbers 
    if (inputValue != null && radioValue != null) return _.round(inputValue, 0) === _.round(radioValue, 0)

    // If one has value whereas the other doesn't, apparently they are different
    return false;
  }

  // Evaluates if a radio button is selected
  const getIsRadioSelected = (radioOption) => {
    let isSelected = false;
    // if this proforma input has been set before, then we compare the inputValue and inputType with radioOption value and type.
    const isSameAsSelectedRadioValue = compareInputValueToRadioValue(inputValue, radioOption.value);
    const isSameAsSelectedRadioType = radioOption.type === inputValueType;
    // If both inputValue and inputType are the same with radioOption value/type, then we mark this radio as selected.
    isSelected = isSameAsSelectedRadioValue && isSameAsSelectedRadioType;

    return isSelected;
  }
  const customInputSubHeader = (inputValue) => {
    if (inputValue) {
      if (inputGroup === 'taxes') {
        if (ancillary.avm) {
          return `${ToPercentageStringWithDecimals(inputValue / ancillary.avm, 1)} of AVM`;
        } else {
          return '';
        }
      } else if (inputGroup === 'rent') {
        return `${ToCurrencyStringWithDecimals(inputValue / ancillary.sqft, 2)}/sqft`;
      } else {
        return `${ToCurrencyStringWithDecimals(inputValue / ancillary.sqft, 1, true)}/sqft`;
      }
    } else {
      return '';
    }
  }

  const extraButton = (inputType) => {
    if (inputType) {
      if (inputGroup === 'taxes' && inputType === Enums.TaxesType.Model && !userHasUnderwriteReadOnlyRole) {
        if (!isRefreshingModelTax) {

          const tooltip = disableTaxRefresh ? "Estimated taxes not affected by price" : "Refresh Model Tax"

          return (
            <Grow in={true}>
              <Tooltip title={tooltip} placement="top">
                <span>
                  <IconButton size="small" onClick={(event) => onRefreshModelTaxClick(event)} classes={{ disabled: classes.refreshTaxesDisabled }} disabled={isDisabled || disableTaxRefresh || isRefreshingModelTax || inputValueType !== _valueTypeEnum.current.Model}>
                    <RefreshIcon fontSize="inherit"/>
                  </IconButton>
                </span>
              </Tooltip>
            </Grow>
          )
        }
        else{
          return <CircularProgress size={15} style={{ marginLeft: '5px' }} />
        }
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  const customInputTooltip = () => {
    if (inputGroup === 'repairEstimate') {
      const modelValue = radioValuesArray.find(el => el.type === Enums.RepairEstimateType.Model);
      if (modelValue?.value == null) return '';
      return `75% of Modeled Value: ${ToCurrencyString(modelValue.value * .75)}`;
    }
    return '';
  }

  const hasValue = customInput != null && customInput !== '';
  const hasValueError = inputValueType === _valueTypeEnum.current.UserDictate && ((required && !hasValue) || (hasValue && valuationInputSetting != null && (customInput > valuationInputSetting.max || customInput < valuationInputSetting.min)));
  useEffect(() => {
    setInputLimitError(inputGroup, hasValueError);
  }, [hasValueError]);
  const errorMessage = hasValueError 
    ? required
      ? 'Value is required'
      : `Value must be from ${formatValue(valuationInputSetting.min, { type: 'money', decimals: 0 })} to ${formatValue(valuationInputSetting.max, { type: 'money', decimals: 0 })}`
    : '';
  return (
    <>
      <div className={classes.inputTitle}>
        <Typography variant='body2' className={classNames(
          (hasValueError || (required && (inputValueType === null || (inputValueType === _valueTypeEnum.current.UserDictate && customInput === null))))
            ? classes.inputsSubheader
            : classes.inputsSubheaderSelected,
          isDisabled && classes.disabledText)}>
          {inputSubheader}
        </Typography>
        {isLoading &&
          <CircularProgress size={15} />
        }
      </div>
      <div>
        {/* Radio Buttons */}
        <FormControl component='fieldset' style={{ width: '100%' }}>     
          <RadioGroup
            name={inputGroup}
            className={classes.radioGroup}
            onChange={(event) => onSelectedRadioChanged(event)}
          >
            {/* Custom radio */}
            <FormControlLabel
              classes={{ root: (inputValueType === _valueTypeEnum.current.UserDictate) ? classes.selectedFormControlLabelRoot : classes.formControlLabelRoot, label: classes.formControlLabel }}
              value={_valueTypeEnum.current.UserDictate}
              onClick={event => onCustomLabelClick(event)}
              label={
                <div className={classes.formControlLabelContent}>
                  <Tooltip title={customInputTooltip()} placement="bottom-start">
                    <span className={classes.formControlLabelContentCol1}>Custom</span>
                  </Tooltip>
                  {/* <span className={classes.formControlLabelContentCol1}>{`${findLabelByInputGroupName(inputGroup)} Custom`}</span> */}
                  <span className={`${classes.formControlLabelContentCol2} ${classes.ancillary}`}>
                    {customInputSubHeader(customInput)}
                  </span>
                  <span className={classes.formControlLabelContentCol3}>
                    {/* Custom radio input*/}
                    <Tooltip title={errorMessage}>
                      <NumberFormat disabled={isDisabled}
                        thousandSeparator={true} decimalScale={0}
                        onChange={event => onTextFieldValueChanged(event)} // Change back to onChange from onValueChange because onValueChange will get triggered even when we are initializing input field value, instead of getting fired only from input change 
                        value={customInput}
                        customInput={Input}
                        classes={{ root: classes.radioInputRoot, input: classNames(classes.radioInputsTextField, hasValueError && classes.radioInputsTextFieldError) }}
                        prefix={'$'}
                        inputRef={_textInputRef}
                        onClick={event => onCustomInputClick(event)}
                        onKeyDown={event => onTextFieldKeyDown(event)}
                        isAllowed={({ value }) => {
                          return !value || (value.length <= (valuationInputSetting ? valuationInputSetting.max.toString().length : 8) && value >= 0)
                        }}
                      />
                    </Tooltip>
                  </span>
                </div>}
              control={
                <Radio
                  disabled={isDisabled}
                  checked={inputValueType === _valueTypeEnum.current.UserDictate}
                  color='default'
                  // classes={{ root: classes.inputsRadio }}
                  tabIndex={-1}
                  icon={<RadioButtonUncheckedIcon style={{ fontSize: '18px' }} />}
                  checkedIcon={<RadioButtonCheckedIcon style={{ fontSize: '18px' }} />}
                />}
            />
            {/* Data model radios */}
            {radioValuesArray && radioValuesArray.map((element, index) => {
              const isSelected = getIsRadioSelected(element);
              return (
                <FormControlLabel
                  key={index}
                  classes={{ root: isSelected ? classes.selectedFormControlLabelRoot : classes.formControlLabelRoot, label: classes.formControlLabel }}
                  value={`${element.type}`}
                  label={
                    <div className={classes.formControlLabelContent}>
                      {element.tooltip ?
                        <Tooltip title={element.tooltip} placement="bottom-start">
                          <span className={classes.formControlLabelContentCol1}>
                            <span>{`${element.label}`}</span>
                            <span>{extraButton(element.type)}</span>
                          </span>
                        </Tooltip>
                        : <span className={classes.formControlLabelContentCol1}>
                            <span>{`${element.label}`}</span>
                            <span>{extraButton(element.type)}</span>
                        </span>
                      }
                      <span className={`${classes.formControlLabelContentCol2} ${classes.ancillary}`}>
                        {customInputSubHeader(element.value)}
                      </span>
                      <span className={classes.formControlLabelContentCol3}>
                        {`${element.value !== null ? ToCurrencyString(element.value) : ''}`}
                      </span>
                    </div>
                  }
                  control={
                    <Radio
                      disabled={isDisabled}
                      checked={isSelected}
                      color='default'
                      tabIndex={-1}
                      icon={<RadioButtonUncheckedIcon style={{ fontSize: '18px' }} />}
                      checkedIcon={<RadioButtonCheckedIcon style={{ fontSize: '18px' }} />}
                    />}
                />

              )
            })}
          </RadioGroup>
        </FormControl>
      </div>
      <Divider className={classes.divider} />
    </>
  )
};

const mapStateToProps = (state, ownProps) => {
  let valuationInputSetting = null;
  const { inputValidation } = state.appState.appSettings;
  if (inputValidation) {
    const { isPostAcquisitionUnderwrite, inputGroup } = ownProps;
    const type = isPostAcquisitionUnderwrite ? 'postAcq' : 'acq';
    valuationInputSetting = inputValidation[type]?.[inputGroup];
  };

  return {
    valuationInputSetting
  }
};

export default compose(
  withStyles(styles),
  connect(
    mapStateToProps
  )
)(PropertyUWProformaInput);

